<!-- Show reset controls at top -->
@if (canAnyLinesBeSelected()) {
  <div class="reset-controls">  
    <button matButton="filled" color="secondary" (click)="selectResets(true)">
      <mat-icon>select_all</mat-icon>
      <span>Select All</span>
    </button>

    <button matButton="filled" color="secondary" (click)="selectResets(false)">
      <mat-icon>clear</mat-icon>
      <span>Deselect All</span>
    </button>
        
    @if (canReset() && !resetRunning()) {
      <button matButton="filled" color="accent" [matMenuTriggerFor]="resetMenu">
          <mat-icon>refresh</mat-icon>
          <span>Reset {{ resetLineCount }} Line(s) To ⏷</span>
      </button>
      <mat-menu #resetMenu="matMenu">
        @if (canResetLineWithResetType(ShipmentLineResetType.StreamingShipmentLineForLicensing)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.StreamingShipmentLineForLicensing)">Licensing (Streaming)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.StreamingShipmentLineForPackageAssignment)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.StreamingShipmentLineForPackageAssignment)">Package Assignment (Streaming)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.CinelinkShipmentLineForLicensing)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.CinelinkShipmentLineForLicensing)">Licensing (Cinelink)</button>
        }
        
        @if (canResetLineWithResetType(ShipmentLineResetType.CvdShipmentLineForBurning)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.CvdShipmentLineForBurning)">Burning (CVD)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.CvdShipmentLineForPinPlay)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.CvdShipmentLineForPinPlay)">PinPlay (CVD)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.CvdShipmentLineForAuthoring)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.CvdShipmentLineForAuthoring)">Authoring (CVD)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.CvdShipmentLineForTranscoding)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.CvdShipmentLineForTranscoding)">Transcoding (CVD)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.LegacyElectronicDeliveryLineForEncryption)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.LegacyElectronicDeliveryLineForEncryption)">Encryption (Legacy eDelivery)</button>
        }

        @if (canResetLineWithResetType(ShipmentLineResetType.AdebsDriveShipmentLineForEncryption)) {
          <button mat-menu-item (click)="resetLines(ShipmentLineResetType.AdebsDriveShipmentLineForEncryption)">Encryption (ADEBS Drive Shipment)</button>
        }
      </mat-menu>
    }

    @if (resetRunning()) {
      <insight-loading-spinner [loadingMessage]="'Running Reset...'">
      </insight-loading-spinner>
    }
  </div>
}

<!-- simplified table view that wraps better with elements -->
<div class="order-shipment-line-container" [class.showing-reset-controls]="canAnyLinesBeSelected()">
  @for (line of orderShipment().lines; track line.lineNumber) {
    <div class="order-shipment-line" [class.reset-selected]="canResetLine(line) && line.resetSelected">
      <!-- Combine the fields to the left side div to prevent really long titles from compressing them all together -->
      <div class="line-left-side">
         <!-- Only show if any lines can be selected for reset -->
         @if (canAnyLinesBeSelected()) { 
           <div class="reset-box">
             @if (canResetLine(line)) { 
               <mat-checkbox [(ngModel)]="line.resetSelected">        
               </mat-checkbox>
             }
           </div>
         }

         <!-- line number. Specify width so X, XX, and XXX will all line up better like a grid-->
         <div class="order-column line-number-column">{{ line.lineNumber }}</div>

         <!-- Line status-->
         <div class="order-column order-status-column">
           @if (line.isCancelled) {
             <span>Cancelled</span>
           } @else {
             <!-- active status-->
               <span class="active-status">
                 <span>{{orderUtilityService.mapOrderDetailsStatus(line.status, orderShipment().orderType)}}</span>
                 @if (line.isProcessing) {
                   <mat-icon matTooltip="Line is processing" aria-hidden="false" aria-label="offline_bolt">offline_bolt</mat-icon>
                 }
               </span>

             <!-- Encryption station (only for ADEBS and if line is encrypting)-->
             @if (line.status === lineStatusEnums.Encrypting && line.encryptStation && orderShipment().orderType === orderTypeEnum.Adebs) {
               <span class="encrypt-station">
                 <span matTooltip="Encryption Station">{{line.encryptStation}}</span>
               </span>
             }
           }
         </div>

         @if (orderShipmentHasLineErrors()) {
           <div class="order-column error-column">
             @if (line.errors.length > 0) {
               <insight-order-issues [errors]="line.errors" [showIssuesLabel]="false"></insight-order-issues>
             }
           </div>
         }

         <!-- ADEBS shows the version code, everything else shows the edition type -->
         @if (orderShipment().orderType === orderTypeEnum.Adebs) {
           <div class="order-column version-code-column" [matTooltip]="convertArcCodeToDescription(line.version)">
             {{ line.version ?? "N/A" }}
           </div>
         } @else {
           <div class="order-column edition-type-column" [matTooltip]="line.version + ': ' + convertArcCodeToDescription(line.version)">
             {{ line.editionType ?? "N/A" }}
           </div>
         }

         <div class="order-column language-column">
           <insight-order-line-languages
             [audioLanguages]="line.audioLanguages"
             [subtitleLanguages]="line.subtitleLanguages">
           </insight-order-line-languages>
         </div>

         <!-- DRM Type. Streaming server can have multiple DRM types going in one order -->
         @if (orderShipment().orderType === orderTypeEnum.StreamingServer) {
           <div class="order-column drm-column">{{ line.drmType }}</div>
         }
      </div>    

      <div class="order-column product-column">
        <span [copyable]="line.productNumber" copyLabel="product number">{{line.productNumber}}</span>
        <button class="product-button" mat-button color="primary" (click)="linkToPubAdmin(line.productNumber)">{{ line.title }}</button>
      </div>
        
      <!-- license date range. If all the licenses are the same, they won't be shown here and should
            be shown at a higher level in an overview. 
            It would be nice to have this for ADEBs, orders, but we currently don't bring back that data -->
      @if (orderShipment().orderType !== orderTypeEnum.Adebs && isAllLicensesIdentical() === false) {
        <div class="order-column order-license-range">  
          <div>{{line.validFrom | date:'M-d-yyyy'}}</div>
          <strong>to</strong>
          @if (line.validTo) {
            <div>{{line.validTo | date:'M-d-yyyy'}}</div>
          } @else {
            <span class="no-end-date">None</span>
          }
        </div>
      }
    </div>
  }
</div>
